import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0
    };
    this.maxRetries = props.maxRetries || 3;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: this.state.retryCount + 1
      });
    }
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.handleRetry,
          this.handleReset
        );
      }

      // Default error UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">
              <svg 
                width="48" 
                height="48" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            
            <h2 className="error-title">
              {this.props.title || 'Something went wrong'}
            </h2>
            
            <p className="error-message">
              {this.props.message || 'An unexpected error occurred while loading this component.'}
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-details">
                <summary>Error Details (Development)</summary>
                <pre className="error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            
            <div className="error-actions">
              {this.state.retryCount < this.maxRetries && (
                <button 
                  className="btn btn-primary"
                  onClick={this.handleRetry}
                >
                  Try Again ({this.maxRetries - this.state.retryCount} attempts left)
                </button>
              )}
              
              <button 
                className="btn btn-secondary"
                onClick={this.handleReset}
              >
                Reset Component
              </button>
              
              {this.props.onReportError && (
                <button 
                  className="btn btn-outline"
                  onClick={() => this.props.onReportError(this.state.error)}
                >
                  Report Error
                </button>
              )}
            </div>
            
            {this.props.showRefreshHint && (
              <p className="error-hint">
                If the problem persists, try refreshing the page or contact support.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundaries
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for error reporting
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  const reportError = React.useCallback((error, errorInfo = {}) => {
    setError({ error, errorInfo, timestamp: new Date().toISOString() });
    
    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error reported via useErrorHandler:', error, errorInfo);
    }
    
    // Could integrate with error reporting service here
    // e.g., Sentry, LogRocket, etc.
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  return { error, reportError, clearError };
};

// Specific error boundary for API errors
export const ApiErrorBoundary = ({ children, onRetry, ...props }) => {
  const fallback = (error, retry, reset) => (
    <div className="api-error-boundary">
      <div className="api-error-content">
        <div className="error-icon api-error-icon">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        
        <h3 className="api-error-title">
          Connection Error
        </h3>
        
        <p className="api-error-message">
          Unable to load data from the server. Please check your connection and try again.
        </p>
        
        <div className="api-error-actions">
          <button className="btn btn-primary" onClick={onRetry || retry}>
            Retry Connection
          </button>
          <button className="btn btn-secondary" onClick={reset}>
            Reset
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary fallback={fallback} {...props}>
      {children}
    </ErrorBoundary>
  );
};

// Chart-specific error boundary
export const ChartErrorBoundary = ({ children, ...props }) => {
  const fallback = (error, retry, reset) => (
    <div className="chart-error-boundary">
      <div className="chart-error-content">
        <div className="error-icon chart-error-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 3v18h18"/>
            <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"/>
          </svg>
        </div>
        
        <h4 className="chart-error-title">Chart Error</h4>
        <p className="chart-error-message">
          Unable to render chart. Data may be malformed or missing.
        </p>
        
        <button className="btn btn-sm btn-primary" onClick={retry}>
          Retry
        </button>
      </div>
    </div>
  );

  return (
    <ErrorBoundary fallback={fallback} {...props}>
      {children}
    </ErrorBoundary>
  );
};

// Table-specific error boundary
export const TableErrorBoundary = ({ children, ...props }) => {
  const fallback = (error, retry, reset) => (
    <div className="table-error-boundary">
      <div className="table-error-content">
        <div className="error-icon table-error-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M9 1H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2z"/>
            <path d="M19 1h-4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2z"/>
            <path d="M9 11H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2z"/>
            <path d="M19 11h-4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2z"/>
          </svg>
        </div>
        
        <h4 className="table-error-title">Table Error</h4>
        <p className="table-error-message">
          Unable to display table data.
        </p>
        
        <button className="btn btn-sm btn-primary" onClick={retry}>
          Reload Table
        </button>
      </div>
    </div>
  );

  return (
    <ErrorBoundary fallback={fallback} {...props}>
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;